import { register, CloudObject, BrickContext, defineProperty, Relation, Direction } from 'olympe';
import { map, startWith } from 'rxjs/operators';

/**
 * @extends {CloudObject}
 */
export default class DataGridConfig extends CloudObject {

    /**
     * Resolve each time the object changes
     * @param {!BrickContext} $
     * @return {!Observable<!Object>}
     */
    observeObject($) {
        const toObject = () => ({
            'tag': this.getTag(),
            [DataGridConfig.rendererOptionsProp.getTag()]: parseJSON(this.get(DataGridConfig.rendererOptionsProp)),
            [DataGridConfig.valuePathsProp.getTag()]: this.get(DataGridConfig.valuePathsProp) || '',
            [DataGridConfig.rendererTypeProp.getTag()]: this.get(DataGridConfig.rendererTypeProp) || '',
            [DataGridConfig.columnPositionProp.getTag()]: this.get(DataGridConfig.columnPositionProp) || -1,
            [DataGridConfig.columnNameProp.getTag()]: this.get(DataGridConfig.columnNameProp) || '',
            //use to fetch the tag defined in Datagridconfig in draw for sortable and sortableType props
            [DataGridConfig.sortableProp.getTag()]: this.get(DataGridConfig.sortableProp) || '',
            [DataGridConfig.sortererTypeProp.getTag()]: this.get(DataGridConfig.sortererTypeProp) || 'default',
            [DataGridConfig.pinnedProp.getTag()]: this.get(DataGridConfig.pinnedProp) || '',
            [DataGridConfig.pinPositionProp.getTag()]: this.get(DataGridConfig.pinPositionProp) || '',
            [DataGridConfig.columnOptionsProp.getTag()]: parseJSON(this.get(DataGridConfig.columnOptionsProp)),
        });

        return this.observeProperties($).pipe(
            map(() => toObject()),
            startWith(toObject())
        );
    }
}

// Hardcoded tags
register('018857f0c67503c31b61', DataGridConfig);
DataGridConfig.columnPositionProp = defineProperty('018857f2da25300b5b28');
DataGridConfig.columnOptionsProp = defineProperty('018857f6c929fd3cb789');
DataGridConfig.columnNameProp = defineProperty('018857f6cdc2755fc8c1');
DataGridConfig.rendererOptionsProp = defineProperty('018857f76212384b52bc');
DataGridConfig.rendererTypeProp = defineProperty('018857f77405bba4b798');
DataGridConfig.valuePathsProp = defineProperty('018857f788ccf4365805');
DataGridConfig.sortableProp = defineProperty('018c67b719b99c82a0da');
DataGridConfig.sortererTypeProp = defineProperty('018c67b74b8d6ca33a66');
DataGridConfig.pinnedProp = defineProperty('019a6d4f77a393b18027');
DataGridConfig.pinPositionProp = defineProperty('019a77eeb7501f6d681a');

// Parse JSON helper
function parseJSON(json) {
    let object = {};
    try { object = JSON.parse(json); } catch(e) {}
    return object;
}

/**
 * @param {!CloudObject} object
 * @param {string} paths
 * @return {!Array}
 */
export function getValuesFromPaths(object, paths) {
    return paths.split(';')
        .map(path => path.split('::')
            .map(subpath => subpath.trim())
            .filter(subpath => subpath))
        .filter(path => path.length > 0)
        .map(path => getValueFromPath(object, path));
}

/**
 * @param {!CloudObject} object
 * @param {!Array} path
 * @return {!*}
 */
export function getValueFromPath(object, path) {
    let finalObject = object;
    for(let i = 0; i < path.length - 1; i++) {
        if(!finalObject)
            break;
        const relationObject = findRelationFromName(finalObject, path[i]);
        const relation = new Relation(relationObject.getTag(), Direction.DESTINATION);
        finalObject = finalObject.followSingle(relation).executeFromCache();
    }
    return finalObject?.get(findPropertyFromName(finalObject, path[path.length - 1]));
}

/**
 * @param {!CloudObject} object
 * @param {string} paths
 * @return {!Array}
 */
export function getPropertiesFromPaths(object, paths) {
    return paths.split(';')
        .map(path => path.split('::')
            .map(subpath => subpath.trim())
            .filter(subpath => subpath))
        .filter(path => path.length > 0)
        .map(path => getPropertyFromPath(object, path));
}

/**
 * @param {!CloudObject} object
 * @param {!Array} path
 * @return {!*}
 */
function getPropertyFromPath(object, path) {
    let finalObject = object;
    for(let i = 0; i < path.length - 1; i++) {
        if(!finalObject)
            break;
        const relationObject = findRelationFromName(finalObject, path[i]);
        const relation = new Relation(relationObject.getTag(), Direction.DESTINATION);
        finalObject = finalObject.followSingle(relation).executeFromCache();
    }
    return findPropertyFromName(finalObject, path[path.length - 1]);
}

/**
 * @param {!CloudObject} object
 * @param {string} name
 * @return {!Property}
 */
function findPropertyFromName(object, name) {
    const properties = object.getModel().follow(CloudObject.propertyRel).executeFromCache();
    for(let property of properties.values()) {
        if(property.name() === name) {
            return property;
        }
    }
    return null;
}

/**
 * @param {!CloudObject} object
 * @param {string} name
 * @return {!Relation}
 */
function findRelationFromName(object, name) {
    const originOfRelationRel = new Relation('ff02200000000000000a', Direction.DESTINATION);
    const relations = object.getModel().follow(originOfRelationRel).executeFromCache();
    for(let relation of relations.values()) {
        if(relation.name() === name) {
            return relation;
        }
    }
    return null;
}
